import {
  CONTRACTOR_BADGE_GRADING_DECISION,
  type IncludedFinalGrade,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageHeaderWrapper, Text, Title } from "@src/appV2/redesign/system/ui-components";

import { PAGE_HEADER_SPACER_MIN_HEIGHT } from "./constants";

interface BadgeVerificationGradeResultContentProps {
  photoPreviewUrl: string;
  gradeResult: IncludedFinalGrade;
  isSubmitting: boolean;
  onClockIn: () => void;
  onForceSubmit: () => void;
  onRetakePhoto: () => void;
}

function BadgePolicyHint() {
  return (
    <Text variant="body2" color={(theme) => theme.palette.error.main}>
      To verify successfully, make sure your badge is clearly shown in the photo and attached to
      your attire.
    </Text>
  );
}

function PhotoPreview(props: { photoPreviewUrl: string }) {
  return (
    <Box
      sx={(theme) => ({
        width: "100%",
        maxWidth: 300,
        aspectRatio: "3/4",
        borderRadius: theme.shape.borderRadius,
        overflow: "hidden",
        backgroundColor: theme.palette.grey[200],
      })}
    >
      <Box
        component="img"
        src={props.photoPreviewUrl}
        alt="Badge photo"
        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </Box>
  );
}

function SuccessContent(props: { photoPreviewUrl: string; onClockIn: () => void }) {
  return (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "auto",
        }}
      >
        <Stack alignItems="center" textAlign="center" spacing={8}>
          <Title variant="h3" component="h1">
            Thank you for verifying your badge
          </Title>

          <PhotoPreview photoPreviewUrl={props.photoPreviewUrl} />

          <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
            You may now proceed to clock in to your shift
          </Text>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Button fullWidth variant="contained" size="large" onClick={props.onClockIn}>
          Clock in
        </Button>
      </DialogFooter>
    </>
  );
}

function FailureContent(props: {
  photoPreviewUrl: string;
  notes: string | undefined;
  isSubmitting: boolean;
  onForceSubmit: () => void;
  onRetakePhoto: () => void;
}) {
  return (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "auto",
        }}
      >
        <Stack alignItems="center" textAlign="center" spacing={6}>
          <Stack alignItems="center" spacing={2}>
            <Title
              variant="h4"
              component="h1"
              sx={(theme) => ({ color: theme.palette.error.main })}
            >
              {isDefined(props.notes) ? props.notes : "Your badge could not be verified"}
            </Title>

            <BadgePolicyHint />
          </Stack>

          <PhotoPreview photoPreviewUrl={props.photoPreviewUrl} />
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          variant="outlined"
          size="large"
          isLoading={props.isSubmitting}
          onClick={props.onRetakePhoto}
        >
          Retake photo
        </LoadingButton>
        <Button
          fullWidth
          variant="text"
          size="large"
          disabled={props.isSubmitting}
          onClick={props.onForceSubmit}
        >
          Submit anyway
        </Button>
      </DialogFooter>
    </>
  );
}

function Header() {
  return (
    <PageHeaderWrapper variant="tertiary">
      <Stack sx={{ minHeight: PAGE_HEADER_SPACER_MIN_HEIGHT }} />
    </PageHeaderWrapper>
  );
}

export function BadgeVerificationGradeResultContent(
  props: BadgeVerificationGradeResultContentProps
) {
  const { photoPreviewUrl, gradeResult, isSubmitting, onClockIn, onForceSubmit, onRetakePhoto } =
    props;

  const isFailure =
    gradeResult.attributes.decision ===
    CONTRACTOR_BADGE_GRADING_DECISION.INCORRECT_BADGE_SUBMISSION;

  if (isFailure) {
    return (
      <>
        <Header />
        <FailureContent
          photoPreviewUrl={photoPreviewUrl}
          notes={gradeResult.attributes.notes}
          isSubmitting={isSubmitting}
          onForceSubmit={onForceSubmit}
          onRetakePhoto={onRetakePhoto}
        />
      </>
    );
  }

  return (
    <>
      <Header />
      <SuccessContent photoPreviewUrl={photoPreviewUrl} onClockIn={onClockIn} />
    </>
  );
}
